import React, { useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContract';
import { usePoolStatus } from '../../hooks/usePoolStatus';
import { useNotify } from '../../context/NotificationContext';
import { useXtPrice } from '../../hooks/useXtPrice';
import { formatUSDT } from '../../utils/format';
import { POOL_LABELS, POOL_NAMES } from '../../utils/constants';
import { parseError } from '../../utils/ethers';
import { Spinner } from '../common/Loader';
import './PoolRewardsCard.css';

export default function PoolRewardsCard({ userId, onDone }) {
  const { write } = useContracts();
  const { xtPrice } = useXtPrice();
  const { available, loading, currentPool, claimableUSDT, unclaimedRounds, snapshot, refresh } = usePoolStatus(userId);
  const notify = useNotify();
  const [claiming, setClaiming] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Hide entirely if XterraPools isn't deployed yet
  if (!available) return null;

  const hasClaimable = claimableUSDT && claimableUSDT.gt(0);
  const highestQualifying = snapshot ? Number(snapshot.highestQualifyingPool) : 0;
  const claimableXt = xtPrice && xtPrice.gt(0)
    ? claimableUSDT.mul(ethers.utils.parseUnits('1', 18)).div(xtPrice)
    : ethers.BigNumber.from(0);

  async function handleClaim() {
    if (!write?.pools) {
      notify.error('Please connect your wallet on the correct network.');
      return;
    }
    setClaiming(true);
    try {
      const tx = await write.pools.claimPoolIncome();
      notify.info('Claiming pool rewards...');
      await tx.wait();
      notify.success('Pool rewards claimed!');
      refresh();
      if (onDone) onDone();
    } catch (err) {
      notify.error(parseError(err));
    } finally {
      setClaiming(false);
    }
  }

  async function handleRefreshEligibility() {
    if (!write?.pools) {
      notify.error('Please connect your wallet on the correct network.');
      return;
    }
    setRefreshing(true);
    try {
      const tx = await write.pools.refreshMyEligibility();
      notify.info('Refreshing eligibility...');
      await tx.wait();
      notify.success('Eligibility refreshed.');
      refresh();
    } catch (err) {
      notify.error(parseError(err));
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <div className="pool-card">
      <div className="pool-card-header">
        <div className="pool-title">
          <span className="pool-icon">🏆</span>
          <span>Pool Rewards</span>
        </div>
        <div className={`pool-tier tier-${currentPool}`}>
          {POOL_NAMES[currentPool]}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 20 }}>
          <Spinner />
        </div>
      ) : (
        <>
          <div className="pool-body">
            <div className="pool-metric main">
              <div className="pm-label">Claimable Now</div>
              <div className="pm-value">
                {formatUSDT(claimableXt, 4)}
                <span className="pm-unit">XT</span>
              </div>
              <div className="pm-sub">≈ {formatUSDT(claimableUSDT)} USDT · {unclaimedRounds} unclaimed round{unclaimedRounds === 1 ? '' : 's'}</div>
            </div>

            {snapshot && (
              <div className="pool-eligibility">
                <div className="pe-row">
                  <span>Self-investment</span>
                  <strong>{formatUSDT(snapshot.selfInvestment, 0)} USDT</strong>
                </div>
                <div className="pe-row">
                  <span>Legs ≥ $100k</span>
                  <strong>{Number(snapshot.legsWithBusinessMet)}</strong>
                </div>
                <div className="pe-row">
                  <span>Legs with X1</span>
                  <strong>{Number(snapshot.legsWithX1)}</strong>
                </div>
                <div className="pe-row">
                  <span>Legs with X2</span>
                  <strong>{Number(snapshot.legsWithX2)}</strong>
                </div>
                {highestQualifying > currentPool && (
                  <div className="pe-hint">
                    ⚡ You now qualify for <strong>{POOL_LABELS[highestQualifying]}</strong> — refresh to promote.
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="pool-actions">
            <button
              className="btn btn-primary btn-block"
              onClick={handleClaim}
              disabled={claiming || !hasClaimable}
            >
              {claiming ? <><Spinner /> Claiming...</> : hasClaimable ? '🏆 Claim Pool Rewards' : 'Nothing to Claim'}
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={handleRefreshEligibility}
              disabled={refreshing}
              title="Force a fresh eligibility check"
            >
              {refreshing ? <Spinner /> : '↻ Refresh Eligibility'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
